import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, Mail, Eye } from "lucide-react";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { OpportunityDetailsModal } from "@/components/mailbox/OpportunityDetailsModal";

interface NotificationData {
  opportunity_id?: string;
  invitation_id?: string;
  inviter_id?: string;
}

const Mailbox = () => {
  const [userRole, setUserRole] = useState<'talent' | 'recruiter' | null>(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState<{
    opportunityId: string;
    invitationId: string;
  } | null>(null);

  // Fetch user role
  useEffect(() => {
    const fetchUserRole = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;

      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .single();

      if (roles) {
        setUserRole(roles.role);
      }
    };

    fetchUserRole();
  }, []);

  // Fetch notifications based on user role
  const { data: notifications, refetch } = useQuery({
    queryKey: ["notifications", userRole],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return [];

      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching notifications:", error);
        return [];
      }

      return data;
    },
  });

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from("notifications")
        .update({ read: true })
        .eq("id", notificationId);

      if (error) throw error;

      refetch();
      toast.success("Marked as read");
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error("Failed to mark as read");
    }
  };

  const handleQuickResponse = async (invitationId: string, status: 'accepted' | 'declined') => {
    try {
      const { error } = await supabase
        .from('opportunity_invitations')
        .update({
          status,
          ...(status === 'accepted' ? {
            application_status: 'applied',
            application_date: new Date().toISOString()
          } : {})
        })
        .eq('id', invitationId);

      if (error) throw error;

      toast.success(`Invitation ${status}`);
      refetch();
    } catch (error) {
      console.error('Error updating invitation:', error);
      toast.error("Failed to update invitation");
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'accepted':
        return <Badge variant="success" className="bg-green-500">Accepted</Badge>;
      case 'declined':
        return <Badge variant="destructive">Declined</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex items-center gap-2 mb-6">
        <Mail className="h-6 w-6" />
        <h1 className="text-2xl font-semibold">
          {userRole === 'recruiter' ? 'Casting Invitations' : 'Notifications'}
        </h1>
      </div>

      <Card className="w-full max-w-3xl mx-auto">
        <ScrollArea className="h-[600px] w-full rounded-md border p-4">
          {notifications?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <Mail className="h-12 w-12 mb-2" />
              <p>No messages yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {notifications?.map((notification) => {
                const notificationData = notification.data as NotificationData;
                
                return (
                  <Card 
                    key={notification.id} 
                    className={`p-4 ${!notification.read ? 'bg-muted/50' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-medium">{notification.title}</h3>
                          {!notification.read && (
                            <Badge variant="default" className="bg-primary">New</Badge>
                          )}
                          {userRole === 'recruiter' && notificationData?.invitation_id && (
                            getStatusBadge(notification.type)
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-4">
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-2">
                          {notificationData?.opportunity_id && (
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedOpportunity({
                                opportunityId: notificationData.opportunity_id!,
                                invitationId: notificationData.invitation_id!
                              })}
                              className="flex items-center gap-1"
                            >
                              <Eye className="h-4 w-4" />
                              View Opportunity
                            </Button>
                          )}
                          {userRole === 'talent' && notificationData?.invitation_id && notification.type === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleQuickResponse(notificationData.invitation_id!, 'declined')}
                              >
                                Decline
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleQuickResponse(notificationData.invitation_id!, 'accepted')}
                              >
                                Accept & Apply
                              </Button>
                            </>
                          )}
                          {!notification.read && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => markAsRead(notification.id)}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Mark as read
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </Card>

      {selectedOpportunity && (
        <OpportunityDetailsModal
          isOpen={!!selectedOpportunity}
          onClose={() => setSelectedOpportunity(null)}
          opportunityId={selectedOpportunity.opportunityId}
          invitationId={selectedOpportunity.invitationId}
        />
      )}
    </div>
  );
};

export default Mailbox;